import type { Observable, SchedulerLike } from 'rxjs';
import { asyncScheduler } from 'rxjs';
import { filter, map, mergeAll } from 'rxjs/operators';
import type { RequestDataPayload } from '../../actions';
import type {
  RequestStatusHandler,
  RequestStatusWithoutRetry,
} from '../../request-status-handler.service';
import { referencesEqual } from '../../store.helpers';
import type { DatastoreCollectionType } from '../../store.model';

/**
 * Deduplicates requests by comparing them with requests received in the last
 * `windowTime` ms.
 *
 * Requests with ids: (e.g. #objects and #object)
 * If a request asks for a particular object id which has been requested before
 * within the interval, it is removed from the request, and then emitted.
 * If this would result in no object ids remaining, the original request
 * is not emitted. Note that the original request is split such that each
 * output request only contains one id each, all emitted simultaneously.
 * i.e. U1:2:3 => U1,U2,U3
 *
 * Example: With a window size of 3 seconds, a request for user with id 1 (U1),
 * then for ids 2 and 1 (U2:1) two seconds later, then again two seconds later:
 *
 * 0     1     2     3     4     5s
 * U1----------U2:1--------U2:1--|
 *        dedupeRequests(3000)
 * U1----------U2----------U1----|
 *
 * Please visit the `.spec.ts` file for more details + diagrams
 *
 * Requests with queries: (e.g. #list)
 * If a request has the same query as another request within the interval,
 * it is not emitted, including empty query objects.
 *
 * IMPORTANT: Assumes that there is at most one clientRequestId for requests
 * in the input observable. This means deduping must occur BEFORE batching.
 *
 * @param windowTime window interval in milliseconds
 */
export function dedupeRequests<C extends DatastoreCollectionType>(
  windowTime: number,
  dedupedBufferMap: Map<
    string,
    {
      // The original request split into individual payloads as separate ids is kept track here.
      // Any that are deduped are not included.
      readonly splitPayloads: readonly RequestDataPayload<any>[];
      // The list of requests that were deduped on this matching request
      readonly dedupedPayloads: readonly RequestDataPayload<any>[];
      // The current status of the matching request
      readonly statusObject?: RequestStatusWithoutRetry<any>;
      // The time this matching request was buffered
      readonly matchingRequestBufferTime: number;
    }
  >,
  scheduler: SchedulerLike = asyncScheduler,
  // Request status handler instance. Can be undefined for unit testing.
  requestStatusHandler?: RequestStatusHandler,
): (
  source$: Observable<RequestDataPayload<C>>,
) => Observable<RequestDataPayload<C>> {
  if (windowTime === 0) {
    return (source$: Observable<RequestDataPayload<C>>) => source$;
  }

  const compareFn = (
    x: RequestDataPayload<C>,
    y: RequestDataPayload<C>,
  ): boolean => referencesEqual(x.ref, y.ref);

  return (source$: Observable<RequestDataPayload<C>>) =>
    source$.pipe(
      map(req => splitOnIds(req)), // all requests split into ONE id each
      mergeAll(),
      filter(requestPayload => {
        const currTime = scheduler.now();
        let requestDeduped = false;

        for (const clientRequestId of dedupedBufferMap.keys()) {
          const bufferedRequest = dedupedBufferMap.get(clientRequestId);
          if (bufferedRequest) {
            // Clean buffer of completed requests older than dedupe window and errored requests
            // Dont remove unfinished requests, we still need these to propogate the status to deduped requests
            if (
              (bufferedRequest.matchingRequestBufferTime + windowTime <
                currTime &&
                bufferedRequest.statusObject !== undefined) ||
              bufferedRequest.statusObject?.error !== undefined
            ) {
              dedupedBufferMap.delete(clientRequestId);
            } else {
              for (const splitPayload of bufferedRequest.splitPayloads) {
                if (
                  // Check if we should dedupe the request by checking for identical requests in window time
                  bufferedRequest.matchingRequestBufferTime + windowTime >=
                    currTime &&
                  compareFn(splitPayload, requestPayload)
                ) {
                  // If the buffered request has already completed, then propogate the request status to deduped requests
                  if (
                    bufferedRequest.statusObject !== undefined &&
                    requestStatusHandler
                  ) {
                    requestStatusHandler.update(
                      requestPayload,
                      bufferedRequest.statusObject,
                    );
                  }

                  // Add request as a deduped request if:
                  //  - if the matching request is not completed yet (if it's completed, we just propogate the status
                  //    immediately above, no need to keep tracked of the deduped request) OR
                  //  - if the matching request is completed but not errored (We don't want to dedupe errored requests)
                  if (
                    bufferedRequest.statusObject === undefined ||
                    (bufferedRequest.statusObject &&
                      bufferedRequest.statusObject.error === undefined)
                  ) {
                    dedupedBufferMap.set(clientRequestId, {
                      ...bufferedRequest,
                      dedupedPayloads: [
                        ...bufferedRequest.dedupedPayloads,
                        requestPayload,
                      ],
                    });
                    requestDeduped = true;
                  }
                }
              }
            }
          }
        }

        if (requestDeduped) {
          return false;
        }

        // Add request to buffer if it was not deduped
        const bufferedRequest = dedupedBufferMap.get(
          requestPayload.clientRequestIds[0],
        );
        dedupedBufferMap.set(
          // Requests should only have 1 client request id as this is done before batching
          requestPayload.clientRequestIds[0],
          {
            splitPayloads: bufferedRequest
              ? [...bufferedRequest.splitPayloads, requestPayload]
              : [requestPayload],
            dedupedPayloads: bufferedRequest
              ? bufferedRequest.dedupedPayloads
              : [],
            statusObject: bufferedRequest
              ? bufferedRequest.statusObject
              : undefined,
            // All the split payload ids should have the same time since they come from the same client request id
            matchingRequestBufferTime: currTime,
          },
        );
        return true;
      }),
    );
}

/**
 * Splits a single request with more than one object id into multiple requests
 * each with one id in its path
 */
function splitOnIds<C extends DatastoreCollectionType>(
  request: RequestDataPayload<C>,
): readonly RequestDataPayload<C>[] {
  const { path } = request.ref;
  const objectIds = path.ids || [];

  // Don't split requests that don't have `ids` or have a query
  // (Yes requests can have `ids` and other query parameters)
  if (objectIds.length === 0 || request.ref.query !== undefined) {
    return [request];
  }

  return objectIds.reduce((requests: RequestDataPayload<C>[], id) => {
    const splitReq: RequestDataPayload<C> = {
      type: path.collection,
      ref: {
        path: {
          collection: path.collection,
          authUid: path.authUid,
          ids: [id],
        },
      },
      clientRequestIds: request.clientRequestIds,
      resourceGroup: request.resourceGroup,
      isRefetch: request.isRefetch,
    };
    return [...requests, splitReq];
  }, []);
}
